/**
 * Search: a field on the top bar, and the matcher behind it.
 *
 * The ring is a picture of a year, and a picture of a year with two hundred
 * people on it answers "when is Anna's birthday?" slowly. Typing a few letters
 * takes everyone else out of the way — the matches keep their cards, the rest
 * recede to a dim mark — so the ring stays the ring rather than becoming a list.
 *
 * Circle names match too, because "who is in Family?" is the same question
 * asked about a group, and the dock's names are the only other place it could
 * be asked from.
 *
 * The matcher is pure and lives here with the field, since the two are one
 * idea: what counts as a match, and how someone says it.
 */
import type { CalEvent } from '@circle/shared';
import { el } from './dom';
import { isNarrow } from './topbar';

/** How long the field waits after the last keystroke before the ring moves. */
export const SEARCH_DEBOUNCE_MS = 120;

/**
 * Case and accents folded away: "Bjö" finds "Björn", and so does "Bjo".
 *
 * A name is typed by whoever is looking for it, on whatever keyboard they
 * have, and a search that insists on the diacritic is a search that fails the
 * person without the key for it.
 */
export function normalizeText(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
}

/**
 * The query, split into the words that all have to be found somewhere.
 *
 * Every term must match *some* field, not all of them the same one, so "anna
 * family" finds Anna if she is in Family. An empty query has no terms, which
 * is the same thing as no filter.
 */
export function searchTerms(query: string): string[] {
  return normalizeText(query).split(/\s+/).filter(Boolean);
}

/**
 * True when every term is a substring of at least one of the strings given.
 * The terms are `searchTerms`' output — folded already, so the folding is done
 * once per query rather than once per entry.
 */
export function matchesTerms(terms: readonly string[], haystacks: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const fields = haystacks.map(normalizeText);
  return terms.every((term) => fields.some((field) => field.includes(term)));
}

/**
 * The query as a predicate over entries, or `null` for "no filter at all".
 *
 * `circleNames` is the tag id → name map the page already has; an entry is
 * matched on its title and on the names of every circle it is in.
 */
export function eventFilter(
  query: string,
  circleNames: ReadonlyMap<string, string>,
): ((event: CalEvent) => boolean) | null {
  const terms = searchTerms(query);
  if (terms.length === 0) return null;
  return (event) =>
    matchesTerms(terms, [
      event.title,
      ...(event.tagIds ?? []).map((id) => circleNames.get(id) ?? ''),
    ]);
}

/**
 * A key that should land in the search field rather than on the ring.
 *
 * One printable character and no modifier: a letter, a digit, an apostrophe.
 * Space is a printable character and is excluded by name, because the ring
 * pans with it (core/input.ts) and a pan that typed a space instead would be
 * a gesture stolen. Every other ring key — Home, Escape, the arrows, Enter —
 * is a named key more than one character long and never matches.
 */
export function isTypeAheadKey(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.key === ' ' || event.key === 'Spacebar') return false;
  return [...event.key].length === 1;
}

/** Focus is already somewhere a keystroke means something else. */
export function isEditing(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * Nothing modal is up. A dialog has taken the whole page over and its own
 * fields are what typing is for; stealing the keystroke to a field behind it
 * would type into something nobody can see.
 */
export function noDialogOpen(doc: Document): boolean {
  return !doc.querySelector('dialog[open], .dialog');
}

export interface SearchFieldOptions {
  /** The query, after the debounce. Cleared fires at once. */
  onChange: (query: string) => void;
  /** Tests drive this to zero; the bar leaves it alone. */
  debounceMs?: number;
  placeholder?: string;
}

/**
 * The field itself: an input, a clear button once there is something to clear,
 * and — on a phone, where a field of its own would push the actions off the
 * edge — an icon button that opens into one.
 */
export class SearchField {
  readonly root: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly clearButton: HTMLButtonElement;
  private readonly openButton: HTMLButtonElement;
  private readonly onChange: (query: string) => void;
  private readonly debounceMs: number;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private last = '';
  private narrow = false;
  private expanded = true;

  constructor(options: SearchFieldOptions) {
    this.onChange = options.onChange;
    this.debounceMs = options.debounceMs ?? SEARCH_DEBOUNCE_MS;

    this.input = el('input', {
      class: 'search__input',
      type: 'search',
      name: 'q',
      placeholder: options.placeholder ?? 'Search',
      'aria-label': 'Search entries and circles',
      autocomplete: 'off',
      spellcheck: 'false',
    });
    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('keydown', (event) => this.onKeyDownInField(event));

    this.clearButton = el('button', {
      class: 'search__clear',
      type: 'button',
      'aria-label': 'Clear search',
      'data-role': 'clear-search',
      text: '×',
      onClick: () => {
        this.set('');
        this.input.focus();
      },
    });
    this.clearButton.hidden = true;

    this.openButton = el('button', {
      class: 'btn btn--small search__open',
      type: 'button',
      'aria-label': 'Search',
      'aria-expanded': 'false',
      'data-role': 'open-search',
      text: '⌕',
      onClick: () => this.expand(),
    });
    this.openButton.hidden = true;

    this.root = el(
      'div',
      { class: 'search', role: 'search' },
      this.openButton,
      el('div', { class: 'search__box' }, this.input, this.clearButton),
    );
    this.narrow = isNarrow();
    this.expanded = !this.narrow;
    this.paint();
  }

  /** What the ring is filtered by right now, debounce already spent. */
  get value(): string {
    return this.input.value;
  }

  /** Set the query from outside and tell the page at once. */
  set(query: string): void {
    this.input.value = query;
    this.flush();
  }

  focus(): void {
    this.expand();
    this.input.focus();
  }

  /**
   * On a phone the bar has room for the name, the year and the actions, and
   * that is all: the field folds to one icon and opens over the row when it is
   * wanted. Emptied, it folds itself away again.
   */
  setNarrow(narrow: boolean): void {
    if (narrow === this.narrow) return;
    this.narrow = narrow;
    this.expanded = !narrow || this.input.value !== '';
    this.paint();
  }

  /** The type-anywhere listener: every printable key goes to the field. */
  attach(target: Window = window): () => void {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isTypeAheadKey(event)) return;
      if (isEditing(event.target)) return;
      if (!noDialogOpen(this.input.ownerDocument)) return;
      // Not prevented: the character the key stands for lands in the field
      // that has just taken the focus, which is the whole point of the trick.
      this.focus();
    };
    target.addEventListener('keydown', onKeyDown);
    return () => target.removeEventListener('keydown', onKeyDown);
  }

  destroy(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.root.remove();
  }

  private expand(): void {
    if (this.expanded) return;
    this.expanded = true;
    this.paint();
  }

  private onKeyDownInField(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // Escape in a field is "put this away", and the ring's own Escape —
      // deselect — is not what someone who has just typed a name wants.
      event.stopPropagation();
      event.preventDefault();
      this.set('');
      this.input.blur();
      if (this.narrow) {
        this.expanded = false;
        this.paint();
      }
      return;
    }
    if (event.key === 'Enter') event.preventDefault();
  }

  private schedule(): void {
    this.paintClear();
    if (this.timer !== undefined) clearTimeout(this.timer);
    if (this.debounceMs <= 0) {
      this.flush();
      return;
    }
    this.timer = setTimeout(() => this.flush(), this.debounceMs);
  }

  private flush(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.paintClear();
    const next = this.input.value;
    if (next === this.last) return;
    this.last = next;
    this.onChange(next);
  }

  private paint(): void {
    this.root.dataset.narrow = this.narrow ? 'true' : 'false';
    this.root.dataset.expanded = this.expanded ? 'true' : 'false';
    this.openButton.hidden = !this.narrow || this.expanded;
    this.openButton.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    this.paintClear();
  }

  private paintClear(): void {
    this.clearButton.hidden = this.input.value === '';
  }
}
